import React, { useState, useEffect, useMemo } from 'react';
import { ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import './guidedDashboard.css';

const API_URL = 'https://live1.finstrats.com/guidedGreaterThanfifteen';

// Grouped Columns Configuration
const METRIC_GROUPS = [
  {
    header: 'Revenue',
    keyPrefix: 'rev',
    columns: [
      { key: 'rev_actual', label: 'Actual', type: 'number', align: 'center', isBlue: true },
      { key: 'rev_p1_guided', label: 'Guided', type: 'number', align: 'center', baseKey: 'rev_actual' },
      { key: 'rev_p1_lr', label: 'Projected', type: 'number', align: 'center', baseKey: 'rev_actual' },
      { key: 'rev_delta', label: 'Delta', type: 'number', align: 'center', isDelta: true, baseKey: 'rev_p1_lr', isLastInGroup: true },
    ]
  },
  {
    header: 'EBITDA',
    keyPrefix: 'ebitda',
    columns: [
      { key: 'ebitda_actual', label: 'Actual', type: 'number', align: 'center', isBlue: true },
      { key: 'ebitda_p1_guided', label: 'Guided', type: 'number', align: 'center', baseKey: 'ebitda_actual' },
      { key: 'ebitda_p1_lr', label: 'Projected', type: 'number', align: 'center', baseKey: 'ebitda_actual' },
      { key: 'ebitda_delta', label: 'Delta', type: 'number', align: 'center', isDelta: true, baseKey: 'ebitda_p1_lr', isLastInGroup: true },
    ]
  },
  {
    header: 'Margin',
    keyPrefix: 'margin',
    columns: [
      { key: 'ebitda_margin_actual', label: 'Actual', type: 'percent', align: 'center', isBlue: true },
      { key: 'ebitda_margin_p1_guided', label: 'Guided', type: 'percent', align: 'center', baseKey: 'ebitda_margin_actual' },
      { key: 'ebitda_margin_p1_lr', label: 'Projected', type: 'percent', align: 'center', baseKey: 'ebitda_margin_actual' },
      { key: 'margin_delta', label: 'Delta', type: 'percent', align: 'center', isDelta: true, baseKey: 'ebitda_margin_p1_lr', isLastInGroup: true },
    ]
  },
  {
    header: 'PAT',
    keyPrefix: 'pat',
    columns: [
      { key: 'pat_actual', label: 'Actual', type: 'number', align: 'center', isBlue: true },
      { key: 'pat_p1_guided', label: 'Guided', type: 'number', align: 'center', baseKey: 'pat_actual' },
      { key: 'pat_p1_lr', label: 'Projected', type: 'number', align: 'center', baseKey: 'pat_actual' },
      { key: 'pat_delta', label: 'Delta', type: 'number', align: 'center', isDelta: true, baseKey: 'pat_p1_lr', isLastInGroup: true },
    ]
  },
  {
    header: 'Capex',
    keyPrefix: 'capex',
    columns: [
      { key: 'capex_actual', label: 'Actual', type: 'number', align: 'center', isBlue: true },
      { key: 'capex_p1_guided', label: 'Guided', type: 'number', align: 'center', baseKey: 'capex_actual' },
      { key: 'capex_p1_lr', label: 'Projected', type: 'number', align: 'center', baseKey: 'capex_actual' },
      { key: 'capex_delta', label: 'Delta', type: 'number', align: 'center', isDelta: true, baseKey: 'capex_p1_lr' }, // Very last column
    ]
  }
];

const GuidedDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sorting and Filtering State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error('Failed to fetch guided data');
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle Sort Request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  // Handle Filter Input Change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Memoized Data Processing
  const processedData = useMemo(() => {
    // 1. Calculate the Deltas
    let enrichedData = data.map((row) => ({
      ...row,
      rev_delta: (row.rev_p1_guided != null && row.rev_p1_lr != null) ? (row.rev_p1_guided - row.rev_p1_lr) : null,
      ebitda_delta: (row.ebitda_p1_guided != null && row.ebitda_p1_lr != null) ? (row.ebitda_p1_guided - row.ebitda_p1_lr) : null,
      margin_delta: (row.ebitda_margin_p1_guided != null && row.ebitda_margin_p1_lr != null) ? (row.ebitda_margin_p1_guided - row.ebitda_margin_p1_lr) : null,
      pat_delta: (row.pat_p1_guided != null && row.pat_p1_lr != null) ? (row.pat_p1_guided - row.pat_p1_lr) : null,
      capex_delta: (row.capex_p1_guided != null && row.capex_p1_lr != null) ? (row.capex_p1_guided - row.capex_p1_lr) : null,
    }));

    // 2. Apply Filters
    Object.keys(filters).forEach((key) => {
      const filterValue = filters[key]?.toString().toLowerCase().trim();
      if (filterValue) {
        enrichedData = enrichedData.filter((row) => {
          const cellValue = row[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(filterValue);
        });
      }
    });

    // 3. Apply Sorting
    if (sortConfig.key) {
      enrichedData.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return enrichedData;
  }, [data, sortConfig, filters]);

  // Unified cell renderer for numeric and percentage formatting
  const renderCell = (row, col) => {
    const val = row[col.key];

    if (val === null || val === undefined) {
      return <span style={{ color: '#9ca3af' }}>NA</span>;
    }
    
    let formattedVal = val;
    if (col.type === 'number') {
      formattedVal = val.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    } else if (col.type === 'percent') {
      formattedVal = `${val.toFixed(2)}%`;
    }

    if (col.isDelta && val > 0) formattedVal = `+${formattedVal}`;

    // Percentage Calculation Inline
    let pctNode = null;
    if (col.baseKey) {
      const baseVal = row[col.baseKey];
      if (baseVal !== null && baseVal !== undefined && baseVal !== 0) {
        let pct = 0;
        if (col.isDelta) {
          pct = (val / Math.abs(baseVal)) * 100;
        } else {
          pct = ((val - baseVal) / Math.abs(baseVal)) * 100;
        }

        let pctColor = '#9ca3af'; // Grey for near-zero
        if (pct > 0.05) pctColor = '#16a34a';
        else if (pct < -0.05) pctColor = '#dc2626';

        const pctText = pct > 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`;

        pctNode = (
          <span style={{ color: pctColor, fontSize: '10px', marginLeft: '4px', fontWeight: '500' }}>
            ({pctText})
          </span>
        );
      }
    }

    const mainColor = col.isDelta 
      ? (val > 0 ? '#16a34a' : val < 0 ? '#dc2626' : '#111827')
      : (col.isBlue ? '#2563eb' : '#111827');
      
    const fontWeight = (col.isDelta || col.type === 'percent') ? '600' : '500';

    return (
      <span style={{ color: mainColor, fontWeight }}>
        {formattedVal}
        {pctNode}
      </span>
    );
  };

  const totalColumns = 2 + METRIC_GROUPS.reduce((acc, group) => acc + group.columns.length, 0);

  return (
    <div className="gd-container">
      <div className="gd-table-wrapper">
        <table className="gd-table">
          <thead>
            {/* ROW 1: Super Headers */}
            <tr className="gd-super-header-row">
              <th rowSpan={2} style={{ width: '40px', textAlign: 'center' }} className="gd-border-right">Sl No.</th>
              <th rowSpan={2} style={{ textAlign: 'left', minWidth: '115px' }} className="gd-border-right">
                <div className="gd-th-content">
                  <span className="gd-sortable-header" onClick={() => requestSort('ticker_code')}>
                    Stock
                    <span className="gd-sort-icon">
                      {sortConfig.key === 'ticker_code' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : <ArrowUpDown size={12} className="gd-icon-muted" />}
                    </span>
                  </span>
                </div>
                {/* <div className="gd-filter-wrapper">
                  <input
                    type="text"
                    className="gd-filter-input"
                    placeholder="Filter..."
                    value={filters['ticker_code'] || ''}
                    onChange={(e) => handleFilterChange('ticker_code', e.target.value)}
                  />
                </div> */}
              </th>
              
              {METRIC_GROUPS.map((group, idx) => (
                <th 
                  key={group.keyPrefix} 
                  colSpan={group.columns.length} 
                  className={`gd-super-header ${idx !== METRIC_GROUPS.length - 1 ? 'gd-border-right' : ''}`}
                >
                  {group.header}
                </th>
              ))}
            </tr>

            {/* ROW 2: Metric Columns */}
            <tr className="gd-sub-header-row">
              {METRIC_GROUPS.map((group, gIdx) => (
                group.columns.map((col, cIdx) => (
                  <th 
                    key={col.key} 
                    style={{ textAlign: col.align }} 
                    className={col.isLastInGroup && gIdx !== METRIC_GROUPS.length - 1 ? 'gd-border-right' : ''}
                  >
                    <div className={`gd-th-content ${col.align === 'right' ? 'gd-justify-end' : ''}`}>
                      <span className="gd-sortable-header" onClick={() => requestSort(col.key)}>
                        {col.label}
                        <span className="gd-sort-icon">
                          {sortConfig.key === col.key ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                          ) : <ArrowUpDown size={12} className="gd-icon-muted" />}
                        </span>
                      </span>
                    </div>
                    {/* <div className="gd-filter-wrapper">
                      <input
                        type="text"
                        className="gd-filter-input"
                        placeholder="Filter..."
                        value={filters[col.key] || ''}
                        onChange={(e) => handleFilterChange(col.key, e.target.value)}
                      />
                    </div> */}
                  </th>
                ))
              ))}
            </tr>
          </thead>
          
          <tbody>
            {loading && <tr><td colSpan={totalColumns} className="gd-status-cell">Loading Guided Data...</td></tr>}
            {error && <tr><td colSpan={totalColumns} className="gd-status-cell gd-error">{error}</td></tr>}
            {!loading && !error && processedData.length === 0 && (
              <tr><td colSpan={totalColumns} className="gd-status-cell">No matching data found.</td></tr>
            )}

            {!loading && !error && processedData.map((row, index) => (
              <tr key={row.ticker_code || index} className="gd-row">
                {/* Fixed Columns */}
                <td style={{ textAlign: 'center', color: '#6b7280' }} className="gd-border-right">{index + 1}</td>
                <td style={{ textAlign: 'left' }} className="gd-border-right">
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <a href={`https://live.finstrats.com/stockdetails?ticker=${row.ticker_code}`} target="_blank" rel="noopener noreferrer" className="gd-ticker-link" title="Open in Screener">
                      {row.ticker_code}
                    </a>
                    <a href={row.kite_url} target="_blank" rel="noopener noreferrer" className="gd-icon-link" title="Open in Kite">
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </td>
                
                {/* Dynamically Mapped Metric Cells */}
                {METRIC_GROUPS.map((group, gIdx) => (
                  group.columns.map((col, cIdx) => (
                    <td 
                      key={col.key} 
                      style={{ textAlign: col.align }}
                      className={col.isLastInGroup && gIdx !== METRIC_GROUPS.length - 1 ? 'gd-border-right' : ''}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GuidedDashboard;